import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Users, MessageSquare, TrendingUp, Lock, Check, Sparkles, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CustomerFilters } from '../components/CustomerFilters';
import { CustomerTable } from '../components/CustomerTable';
import { CustomerModal } from '../components/CustomerModal';
import {
  useGetApiWorkspacesWorkspaceIdCustomers,
  getGetApiWorkspacesWorkspaceIdCustomersQueryKey,
} from '@/api/generated/customers/customers';
import { useGetApiWorkspacesWorkspaceIdStores } from '@/api/generated/stores/stores';
import { extractStoresArray, extractArrayData, cn } from '@/lib/utils';
import { useBillingStore } from '@/stores/useBillingStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type { CustomerInfo, StoreItem } from '@/types';

const PREVIEW_CUSTOMERS: CustomerInfo[] = [
  {
    id: 'cust-demo-1',
    name: 'Sarah Jenkins',
    customerName: 'Sarah Jenkins',
    email: 'sarah.j@example.com',
    customerEmail: 'sarah.j@example.com',
    phone: '+1 (555) 234-5678',
    customerPhone: '+1 (555) 234-5678',
    storeId: 'store-demo-1',
    totalConversations: 8,
    lastActiveAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
    lastSeenAtUtc: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
  },
  {
    id: 'cust-demo-2',
    name: 'Marcus Vance',
    customerName: 'Marcus Vance',
    email: 'm.vance@retailco.com',
    customerEmail: 'm.vance@retailco.com',
    phone: '+1 (555) 890-1234',
    customerPhone: '+1 (555) 890-1234',
    storeId: 'store-demo-1',
    totalConversations: 14,
    lastActiveAt: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
    lastSeenAtUtc: new Date(Date.now() - 1000 * 60 * 180).toISOString(),
  },
  {
    id: 'cust-demo-3',
    name: 'Elena Rostova',
    customerName: 'Elena Rostova',
    email: 'elena.rostova@gmail.com',
    customerEmail: 'elena.rostova@gmail.com',
    phone: '+44 20 7946 0912',
    customerPhone: '+44 20 7946 0912',
    storeId: 'store-demo-2',
    totalConversations: 5,
    lastActiveAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    lastSeenAtUtc: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
  },
  {
    id: 'cust-demo-4',
    name: 'David Kim',
    customerName: 'David Kim',
    email: 'david.kim@techflow.io',
    customerEmail: 'david.kim@techflow.io',
    phone: '+1 (555) 432-8765',
    customerPhone: '+1 (555) 432-8765',
    storeId: 'store-demo-1',
    totalConversations: 3,
    lastActiveAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    lastSeenAtUtc: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
  },
  {
    id: 'cust-demo-5',
    name: 'Camila Santos',
    customerName: 'Camila Santos',
    email: 'camila.s@boutique.br',
    customerEmail: 'camila.s@boutique.br',
    phone: '+55 11 98765-4321',
    customerPhone: '+55 11 98765-4321',
    storeId: 'store-demo-2',
    totalConversations: 11,
    lastActiveAt: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
    lastSeenAtUtc: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
  },
];

const PREVIEW_STORES: StoreItem[] = [
  { id: 'store-demo-1', name: 'Main Shopify Store', provider: 'shopify', status: 'Connected' },
  { id: 'store-demo-2', name: 'EU WooCommerce Outlet', provider: 'woocommerce', status: 'Connected' },
];

export const CustomersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState('all');

  // Customer Edit/Add Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('edit');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerInfo | null>(null);

  const billingStatus = useBillingStore((state) => state.status);
  const hasAccess = billingStatus ? Boolean(billingStatus.allowedFeatures?.includes('CustomersPageAccess')) : true;

  const { activeWorkspaceId } = useWorkspaceStore();

  // Local state overlay for newly added/edited customers in demo
  const [localCustomerOverrides, setLocalCustomerOverrides] = useState<CustomerInfo[]>([]);

  // Fetch stores & customers from API (only when authorized)
  const { data: stores } = useGetApiWorkspacesWorkspaceIdStores(activeWorkspaceId!, {
    query: {
      enabled: hasAccess && !!activeWorkspaceId,
    },
  });
  const storesList = (hasAccess ? extractStoresArray(stores) : PREVIEW_STORES) as StoreItem[];

  const customerQueryParams = useMemo(() => ({
    SearchTerm: search.trim() || undefined,
    StoreId: selectedStoreId !== 'all' ? selectedStoreId : undefined,
  }), [search, selectedStoreId]);

  const { data: apiCustomers, isLoading } = useGetApiWorkspacesWorkspaceIdCustomers(
    activeWorkspaceId!,
    customerQueryParams,
    {
      query: {
        enabled: hasAccess && !!activeWorkspaceId,
      },
    }
  );

  // Merge API data with local additions/edits or use preview data
  const customersList = useMemo(() => {
    if (!hasAccess) {
      return PREVIEW_CUSTOMERS;
    }

    const raw = extractArrayData<any>(apiCustomers).map((item) => ({
      ...item,
      lastActiveAt: item.lastActiveAt || item.lastSeenAtUtc,
      totalConversations: item.totalConversations || 0,
    })) as CustomerInfo[];

    const merged = [...raw];

    for (const override of localCustomerOverrides) {
      const idx = merged.findIndex((c) => c.id === override.id);
      if (idx >= 0) {
        merged[idx] = override;
      } else {
        const matchesStore = selectedStoreId === 'all' || override.storeId === selectedStoreId;
        const q = search.toLowerCase();
        const matchesSearch =
          !q ||
          (override.name && override.name.toLowerCase().includes(q)) ||
          (override.customerName && override.customerName.toLowerCase().includes(q)) ||
          (override.email && override.email.toLowerCase().includes(q)) ||
          (override.phone && override.phone.toLowerCase().includes(q));

        if (matchesStore && matchesSearch) {
          merged.unshift(override);
        }
      }
    }
    return merged;
  }, [hasAccess, apiCustomers, localCustomerOverrides, selectedStoreId, search]);

  const totalConversationsSum = useMemo(() => {
    if (!hasAccess) return 41;
    return customersList.reduce((acc, curr) => acc + (curr.totalConversations || 0), 0);
  }, [hasAccess, customersList]);

  const handleOpenAdd = () => {
    if (!hasAccess) {
      navigate('/billing');
      return;
    }
    setModalMode('add');
    setSelectedCustomer(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (cust: CustomerInfo) => {
    if (!hasAccess) {
      navigate('/billing');
      return;
    }
    setModalMode('edit');
    setSelectedCustomer(cust);
    setIsModalOpen(true);
  };

  const handleSaveCustomer = (saved: Partial<CustomerInfo>) => {
    const updated: CustomerInfo = {
      id: saved.id || `cust-${Date.now()}`,
      name: saved.name || saved.customerName || 'Guest Customer',
      customerName: saved.customerName || saved.name || 'Guest Customer',
      email: saved.email || saved.customerEmail || null,
      customerEmail: saved.customerEmail || saved.email || null,
      phone: saved.phone || saved.customerPhone || null,
      customerPhone: saved.customerPhone || saved.phone || null,
      storeId: saved.storeId || storesList[0]?.id,
      totalConversations: saved.totalConversations || 0,
      lastActiveAt: saved.lastActiveAt || new Date().toISOString(),
      lastSeenAtUtc: saved.lastSeenAtUtc || new Date().toISOString(),
    };

    setLocalCustomerOverrides((prev) => {
      const filtered = prev.filter((c) => c.id !== updated.id);
      return [updated, ...filtered];
    });

    if (activeWorkspaceId) {
      queryClient.invalidateQueries({
        queryKey: getGetApiWorkspacesWorkspaceIdCustomersQueryKey(activeWorkspaceId),
      });
    }
  };

  return (
    <div className="relative p-4 sm:p-6 space-y-6 max-w-7xl mx-auto min-h-[calc(100vh-5rem)]">
      {/* Paywall Overlay for Non-Subscribers */}
      {!hasAccess && (
        <div className="absolute inset-0 z-20 flex items-center justify-center p-4 bg-background/20 backdrop-blur-[2px]">
          <div className="w-full max-w-lg rounded-2xl border border-primary/20 bg-card/95 p-6 sm:p-8 shadow-2xl backdrop-blur-xl text-center space-y-5 animate-in fade-in-50 zoom-in-95 duration-200">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary shadow-inner ring-8 ring-primary/5">
              <Lock className="h-7 w-7" />
            </div>

            <div className="space-y-2">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 text-xs font-semibold px-3 py-0.5">
                Recurso Pro
              </Badge>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                Desbloqueie o CRM de Clientes
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Consolide contatos de clientes, histórico de conversas e dados de compras no CRM unificado de todas as suas lojas.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2.5 text-left text-xs bg-muted/40 p-4 rounded-xl border border-border/50">
              <div className="flex items-center gap-2 text-foreground font-medium">
                <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>Dados completos de contato (E-mail, Telefone e histórico)</span>
              </div>
              <div className="flex items-center gap-2 text-foreground font-medium">
                <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>Histórico completo de conversas por cliente</span>
              </div>
              <div className="flex items-center gap-2 text-foreground font-medium">
                <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                <span>Busca rápida por nome, e-mail e loja</span>
              </div>
            </div>

            <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                size="default"
                onClick={() => navigate('/billing')}
                className="w-full sm:w-auto gap-2 font-semibold shadow-md px-6 cursor-pointer"
              >
                <Sparkles className="h-4 w-4" />
                <span>Atualizar para o Pro</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>

            <p className="text-[11px] text-muted-foreground">
              Ativação imediata • Cancele quando quiser em 1 clique
            </p>
          </div>
        </div>
      )}

      {/* CRM Main Content Area (previewed behind blur when locked) */}
      <div className={cn('space-y-6 transition-all duration-200', !hasAccess && 'pointer-events-none select-none filter blur-[3px] opacity-40')}>
        {/* Top Header Banner */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              CRM de Clientes
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Contatos de clientes e histórico de conversas das suas lojas
            </p>
          </div>
        </div>

        {/* Summary Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-card/50 backdrop-blur-sm border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Total de Clientes no CRM</p>
                <p className="text-lg font-bold text-foreground">{customersList.length}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur-sm border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                <MessageSquare className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Total de Conversas</p>
                <p className="text-lg font-bold text-foreground">{totalConversationsSum}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 backdrop-blur-sm border-border/60">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Média de Conversas / Cliente</p>
                <p className="text-lg font-bold text-foreground">
                  {customersList.length > 0
                    ? (totalConversationsSum / customersList.length).toFixed(1)
                    : '0'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer Filters Toolbar */}
        <CustomerFilters
          search={search}
          onSearchChange={setSearch}
          selectedStoreId={selectedStoreId}
          onStoreChange={setSelectedStoreId}
          customers={customersList}
          onAddCustomerClick={handleOpenAdd}
        />

        {/* CRM Customer Data Table */}
        <CustomerTable
          customers={customersList}
          stores={storesList}
          isLoading={hasAccess ? isLoading : false}
          onEditCustomer={handleOpenEdit}
        />
      </div>

      {/* Customer Add/Edit Modal (Only accessible when authorized) */}
      {hasAccess && (
        <CustomerModal
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
          customer={selectedCustomer}
          mode={modalMode}
          onSave={handleSaveCustomer}
        />
      )}
    </div>
  );
};

export default CustomersPage;
